import {AuctionVehicleDetailResponseBody} from '@dms/api/core';

import {ApiError, Nullish} from 'shared/web';

import {SourcingType} from '../../../hooks/useSourcingType';
import {adaptAuctionVehicleToSourcingItem} from '../../../utils/adaptAuctionVehicleToSourcingItem';
import type {SourcingTableQueryResult} from '../SourcingTable';
import type {Row} from '../types/Row';

/** RTK list query slice shared by classifieds and auction user-list endpoints. */
export type SourcingListRtkQuerySlice<TItem> = {
  data?: {data: TItem[]};
  isLoading: boolean;
  isFetching: boolean;
  error?: ApiError | Nullish;
  refetch: () => void;
};

type CreateSourcingTableQueryResultParams = {
  sourcingType: SourcingType;
  enumLookup: ReadonlyMap<string, string>;
  classifiedsQueryResult: SourcingListRtkQuerySlice<Row>;
  auctionsQueryResult: SourcingListRtkQuerySlice<AuctionVehicleDetailResponseBody>;
  count?: SourcingTableQueryResult['count'];
};

export function createSourcingTableQueryResult(
  params: CreateSourcingTableQueryResultParams
): SourcingTableQueryResult {
  const isClassifieds = params.sourcingType === SourcingType.CLASSIFIEDS;
  const activeQuery = isClassifieds ? params.classifiedsQueryResult : params.auctionsQueryResult;

  const data = isClassifieds
    ? (params.classifiedsQueryResult.data?.data ?? [])
    : (params.auctionsQueryResult.data?.data?.map((item) =>
        adaptAuctionVehicleToSourcingItem(item, params.enumLookup)
      ) ?? []);

  return {
    data,
    isLoading: activeQuery.isLoading,
    isFetching: activeQuery.isFetching,
    error: activeQuery.error,
    onRetry: activeQuery.refetch,
    count: params.count ?? data.length,
  };
}

/** Single classifieds user-list query (no auction branch). */
export function createSourcingTableQueryResultFromListQuery(
  query: SourcingListRtkQuerySlice<Row>,
  params?: {count?: SourcingTableQueryResult['count']}
): SourcingTableQueryResult {
  const data = query.data?.data ?? [];

  return {
    data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    onRetry: query.refetch,
    count: params?.count ?? data.length,
  };
}
